import { forwardRef, useEffect } from 'react';
import { Box } from '@mui/material';
import DOMPurify from 'dompurify';

type EditorAreaProps = {
    html: string;
    onUpdate: () => void;
    onPaste: (event: React.ClipboardEvent<HTMLDivElement>) => void;
    disabled?: boolean;
};

// Properly forward the ref with the correct type for the HTMLDivElement
const EditorArea = forwardRef<HTMLDivElement, EditorAreaProps>(({ html, onUpdate, onPaste, disabled }, ref) => {
    useEffect(() => {
        const editor = (ref as React.RefObject<HTMLDivElement>).current;
        if (!editor) return;

        const handleImageClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (target.tagName === 'IMG') {
                target.style.border = '2px solid #000'; // Highlight image when clicked
                target.contentEditable = 'false'; // Make image non-editable, but resizable
                target.style.resize = 'both'; // Allow resizing both horizontally and vertically
                target.style.overflow = 'auto'; // Enable overflow so resizing works
            } else {
                // Remove the border and resizable option when clicking outside the image
                const images = editor.querySelectorAll('img');
                images.forEach((img) => {
                    img.style.border = 'none';
                    img.style.resize = 'none';
                    img.style.overflow = 'visible';
                });
            }
        };

        editor.addEventListener('click', handleImageClick);

        return () => {
            editor.removeEventListener('click', handleImageClick);
        };
    }, [ref]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflowY: 'scroll' }}>
            <div
                role="textbox"
                ref={ref}
                contentEditable={!disabled}
                onBlur={onUpdate}
                onPaste={onPaste}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
                style={{ padding: 2, pointerEvents: disabled ? 'none' : 'auto' }}
            />
        </Box>
    );
});

export default EditorArea;
